import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/shared";
import { BookingSummary, CheckoutFooter, PhoneInput } from "../components/checkout";
import { countries } from "../data/countries";
import { useTranslation } from "../hooks/useTranslation";
import { useErrorNotification } from "../hooks/useErrorNotification";
import ErrorNotification from "../components/shared/ErrorNotification";
import { createCartSession, recordStep1Completion, getSessionId } from "../services/cartSessionService";
import { trackBeginCheckout } from "../services/analyticsService";
import { validatePromotion, readStoredPromo, storePromo } from "../services/promotionService";
import "../styles/checkout/CheckoutStep1.css";
import "../styles/checkout/Checkout.css";
import "../styles/checkout/PhoneInput.css";
import "../styles/shared/BookingComponents.css";

const CheckoutStep1 = () => {
  const navigate = useNavigate();
  const { t, getLocalizedPath } = useTranslation('checkout');
  // Promo code / credit voucher. The applied quote is a preview only; the
  // server recalculates the discount when the order is created.
  const [promoInput, setPromoInput] = useState(() => readStoredPromo()?.code || "");
  const [appliedPromo, setAppliedPromo] = useState(() => readStoredPromo());
  const [promoStatus, setPromoStatus] = useState(null); // null | 'checking' | reason key

  const { showError, hideError, error } = useErrorNotification();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: "",
    licenseExpiry: "",
    licenseMoreThan3Years: false,
    adults: "",
    children: "",
    pets: false,
    numberOfPets: "",
    vatNumber: "",
  });

  // Track begin_checkout on mount for GA4
  useEffect(() => {
    const bookingData = JSON.parse(sessionStorage.getItem("bookingData") || "{}");
    if (bookingData.vehicleId) {
      trackBeginCheckout(bookingData);
    }
  }, []);

  const handleApplyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;

    setPromoStatus("checking");
    try {
      const booking = JSON.parse(sessionStorage.getItem("bookingData") || "{}");
      const nights = Number(booking.nights) || 0;
      // Must match what the server discounts against when the order is created
      // (simple_secure_order.go passes pricing.DiscountedBase), otherwise a
      // stay-length discount makes this preview promise more than is charged.
      const rentalBase =
        Number(booking.priceBreakdown?.discountedBase) ||
        Number(booking.priceBreakdown?.dailyTotal) ||
        (Number(booking.basePrice) || 0) * nights;

      const result = await validatePromotion(code, {
        rentalBase,
        addOnsTotal: Number(booking.addOnsTotal) || 0,
        bookingTotal: Number(booking.totalPrice) || rentalBase,
        nights,
        customerEmail: formData.email,
      });

      if (result.valid) {
        setAppliedPromo(result.promotion);
        storePromo(result.promotion);
        setPromoStatus(null);
        window.dispatchEvent(new Event("bookingUpdated"));
      } else {
        setAppliedPromo(null);
        storePromo(null);
        setPromoStatus(result.reason || "error");
      }
    } catch {
      setAppliedPromo(null);
      storePromo(null);
      setPromoStatus("error");
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    storePromo(null);
    setPromoInput("");
    setPromoStatus(null);
    window.dispatchEvent(new Event("bookingUpdated"));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedData = {
      ...formData,
      [name]: value,
    };
    setFormData(updatedData);

    // Update sessionStorage and trigger booking update event
    sessionStorage.setItem("checkoutStep1Data", JSON.stringify(updatedData));
    window.dispatchEvent(new Event("bookingUpdated"));
  };

  const handleContinue = async (e) => {
    e.preventDefault();

    // Required field validation - licenseExpiry is hidden so not required
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'country', 'adults'];
    const missingFields = requiredFields.filter(field => !formData[field]);

    // License validation
    if (!formData.licenseMoreThan3Years) {
      showError(t('step1.errors.licenseValidation'));
      return;
    }

    // Adults validation - must have at least 1
    if (!formData.adults || parseInt(formData.adults) < 1) {
      showError(t('step1.errors.minimumAdults'));
      return;
    }

    // Total occupancy validation (adults + children ≤ 5)
    const totalOccupancy = parseInt(formData.adults || 0) + parseInt(formData.children || 0);
    const MAX_CAPACITY = 5; // Based on database schema: vehicles.capacity
    if (totalOccupancy > MAX_CAPACITY) {
      showError(t('step1.errors.maximumOccupancy'));
      return;
    }

    // Pets validation
    if (formData.pets && !formData.numberOfPets) {
      missingFields.push('numberOfPets');
    }

    if (missingFields.length > 0) {
      showError(`${t('step1.errors.missingFields')}: ${missingFields.join(', ')}`);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showError(t('step1.errors.invalidEmail'));
      return;
    }

    // Store form data in sessionStorage
    sessionStorage.setItem("checkoutStep1Data", JSON.stringify(formData));

    // Analytics: Create cart session if first time, then record step 1 (non-blocking)
    try {
      const bookingData = JSON.parse(sessionStorage.getItem("bookingData") || "{}");
      if (!getSessionId()) {
        await createCartSession(bookingData);
      }
      recordStep1Completion(formData); // Fire and forget
    } catch (err) {
      // Analytics errors should never block checkout
    }

    // Navigate to the insurance step (final step, where payment happens)
    navigate(getLocalizedPath("/checkout/step3"));
  };

  return (
    <div className="checkout-step1-container">
      {error && (
        <ErrorNotification
          message={error}
          onDismiss={hideError}
        />
      )}
      <Header />

      <div className="main-content-container">
        <div className="form-section">
          <div className="product-info">
            <h1 className="product-title">{t('step1.title')}</h1>
            <p className="product-description">
              {t('step1.subtitle')}
            </p>
          </div>

          <div className="form-container">
            <h2 className="form-title">{t('step1.formTitle')}</h2>

            <form className="checkout-form" onSubmit={handleContinue}>
              <div className="name-fields-container">
                <div className="input-wrapper">
                  <div className="input-field">
                    <div className="input-state-layer">
                      <div className="input-content">
                        <div className="input-text-container">
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder="Enter your first name"
                            className="input-text"
                          />
                        </div>
                        <div className="label-text-container">
                          <label className="label-text">
                            {t('step1.firstName')}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="input-wrapper">
                  <div className="input-field">
                    <div className="input-state-layer">
                      <div className="input-content">
                        <div className="input-text-container">
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            placeholder="Enter your last name"
                            className="input-text"
                          />
                        </div>
                        <div className="label-text-container">
                          <label className="label-text">
                            {t('step1.lastName')}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="contact-fields-container">
                <div className="input-wrapper">
                  <div className="input-field">
                    <div className="input-state-layer">
                      <div className="input-content">
                        <div className="input-text-container">
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder={t('step1.email')}
                            className="input-text"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="input-wrapper">
                  <div className="input-field">
                    <div className="input-state-layer">
                      <div className="input-content">
                        <div className="input-text-container">
                          <PhoneInput
                            value={formData.phone}
                            onChange={handleInputChange}
                            name="phone"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="country-license-row">
                <div className="country-field-container">
                  <div className="input-wrapper">
                    <div className="input-field">
                      <div className="input-state-layer">
                        <div className="input-content">
                          <div className="input-text-container">
                            <select
                              name="country"
                              value={formData.country}
                              onChange={handleInputChange}
                              className="input-text"
                            >
                              <option value="">{t('step1.country')}</option>
                              {countries.map((country) => (
                                <option key={country.code} value={country.code.toLowerCase()}>
                                  {country.name}
                                </option>
                              ))}
                            </select>
                            <svg
                              className="dropdown-icon"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              style={{ pointerEvents: 'none' }}
                            >
                              <path
                                d="M5 7.5L10 12.5L15 7.5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="input-wrapper">
                  <div className="input-field checkbox-input-field">
                    <div className="input-state-layer">
                      <div className="input-content checkbox-content">
                        <label className="checkbox-container">
                          <input
                            type="checkbox"
                            name="licenseMoreThan3Years"
                            checked={formData.licenseMoreThan3Years}
                            onChange={(e) => {
                              const updatedData = {
                                ...formData,
                                licenseMoreThan3Years: e.target.checked
                              };
                              setFormData(updatedData);
                              sessionStorage.setItem("checkoutStep1Data", JSON.stringify(updatedData));
                              window.dispatchEvent(new Event("bookingUpdated"));
                            }}
                          />
                          <span className="checkmark"></span>
                          <span>{t('step1.licenseMoreThan3Years')}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="additional-fields-container">
                <div className="passenger-fields-container">
                  <div className="input-wrapper">
                    <div className="input-field">
                      <div className="input-state-layer">
                        <div className="input-content">
                          <div className="input-text-container">
                            <input
                              type="number"
                              name="adults"
                              value={formData.adults}
                              onChange={handleInputChange}
                              placeholder="1"
                              className="input-text"
                              min="1"
                            />
                          </div>
                          <div className="label-text-container">
                            <label className="label-text">
                              {t('step1.adults')}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper">
                    <div className="input-field">
                      <div className="input-state-layer">
                        <div className="input-content">
                          <div className="input-text-container">
                            <input
                              type="number"
                              name="children"
                              value={formData.children}
                              onChange={handleInputChange}
                              placeholder="0"
                              className="input-text"
                              min="0"
                            />
                          </div>
                          <div className="label-text-container">
                            <label className="label-text">
                              {t('step1.children')}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pets + promo code share one row */}
                <div className="pets-fields-container">
                  <div className="input-wrapper pets-checkbox-wrapper">
                    <div className="input-field checkbox-input-field">
                      <div className="input-state-layer">
                        <div className="input-content checkbox-content">
                          <label className="checkbox-container">
                            <input
                              type="checkbox"
                              name="pets"
                              checked={formData.pets}
                              onChange={(e) => {
                                const updatedData = {
                                  ...formData,
                                  pets: e.target.checked,
                                  numberOfPets: e.target.checked ? (formData.numberOfPets || "1") : ""
                                };
                                setFormData(updatedData);
                                sessionStorage.setItem("checkoutStep1Data", JSON.stringify(updatedData));
                                window.dispatchEvent(new Event("bookingUpdated"));
                              }}
                            />
                            <span className="checkmark"></span>
                            <span>{t('step1.pets')}</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="input-wrapper promo-code-wrapper">
                    <div className="input-field promo-code-field">
                      <div className="input-state-layer">
                        <div className="input-content">
                          <label className="promo-code-label" htmlFor="promoCode">
                            {t('step1.promo.label')}
                          </label>
                          <div className="promo-code-row">
                            <input
                              id="promoCode"
                              type="text"
                              className="promo-code-input"
                              placeholder={t('step1.promo.placeholder')}
                              value={promoInput}
                              disabled={!!appliedPromo}
                              autoComplete="off"
                              autoCapitalize="characters"
                              onChange={(e) => {
                                setPromoInput(e.target.value.toUpperCase());
                                if (promoStatus) setPromoStatus(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleApplyPromo();
                                }
                              }}
                            />
                            {appliedPromo ? (
                              <button type="button" className="promo-code-btn promo-code-btn--remove" onClick={handleRemovePromo}>
                                {t('step1.promo.remove')}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="promo-code-btn"
                                onClick={handleApplyPromo}
                                disabled={!promoInput.trim() || promoStatus === "checking"}
                              >
                                {promoStatus === "checking" ? t('step1.promo.checking') : t('step1.promo.apply')}
                              </button>
                            )}
                          </div>

                          {appliedPromo && (
                            <p className="promo-code-msg promo-code-msg--ok">
                              {t('step1.promo.applied', { code: appliedPromo.code })}
                              {appliedPromo.discount > 0 && ` · −€${Number(appliedPromo.discount).toFixed(2)}`}
                            </p>
                          )}
                          {promoStatus && promoStatus !== "checking" && (
                            <p className="promo-code-msg promo-code-msg--err">
                              {t(`step1.promo.reasons.${promoStatus}`)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {formData.pets && (
                    <div className="input-wrapper pets-number-input">
                      <div className="input-field">
                        <div className="input-state-layer">
                          <div className="input-content">
                            <div className="input-text-container">
                              <input
                                type="number"
                                name="numberOfPets"
                                value={formData.numberOfPets}
                                onChange={handleInputChange}
                                placeholder="1"
                                className="input-text"
                                min="1"
                                max="4"
                              />
                            </div>
                            <div className="label-text-container">
                              <label className="label-text">
                                {t('step1.numberOfPets')}
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* VAT Number Section */}
              <div className="section-container">
                <div className="section-header">
                  <h3 className="section-title">Invoice Information (Optional)</h3>
                </div>
                <div className="section-content">
                  <div className="input-wrapper">
                    <div className="input-field">
                      <div className="input-state-layer">
                        <div className="input-content">
                          <div className="input-text-container">
                            <input
                              type="text"
                              name="vatNumber"
                              value={formData.vatNumber}
                              onChange={handleInputChange}
                              placeholder={t('step1.vatNumber')}
                              className="input-text"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <p className="privacy-notice">
                We only use this info to finalize your booking, your data is
                safe with us
              </p>

              <button
                type="submit"
                className="continue-button"
                onClick={handleContinue}
              >
                <div className="button-content">
                  <div className="button-label">{t('step1.continue')}</div>
                </div>
              </button>
            </form>
          </div>
        </div>

        <div className="booking-container">
          <BookingSummary />
        </div>
      </div>

      <CheckoutFooter />
    </div>
  );
};

export default CheckoutStep1;
